import NumberFlow from "@number-flow/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AuthConfig,
  cn,
  flaggedControls,
  getControlCategory,
  getControlName,
  getValidControlCategory,
  isDataControl,
  isDefensiveControl,
  isEmailControl,
  isEndpointManagementControl,
  isIdentityProviderControl,
  isValidControl,
  isVulnerabilityManagementControl,
  queries,
  useAccountControls,
  useAuthConfig,
} from "@theprelude/core";
import {
  ArrowRight,
  ControlIcon,
  IconButton,
  MailIcon,
  NetworkIcon,
  ResourceTypeIcon,
  ServerIcon,
  UserIcon,
} from "@theprelude/ds";
import {
  Control,
  ControlCategories,
  ControlCategory,
  ValidControlCategory,
  ValidControlCode,
} from "@theprelude/sdk";
import without from "lodash/without";
import { Masonry, RenderComponentProps } from "masonic";
import { ReactNode, useMemo, FC, SVGProps, ComponentType } from "react";
import { createQuery, enhanceQueryKey } from "@/lib/create-query";
import { tb } from "@/lib/tracking";
import { getControlId, useExceptedFilters } from "@/queries/control-monitor";
import type { ExecutiveSummaryTab } from "@/routes/_app.executive-summary";
import { DefaultTooltip } from "../default-tooltip";
import { hasFeatures } from "../flag/flag";
import { OverlaySchema } from "../overlays";
import { ExecutiveBanner } from "./executive-banner";
import { useExecutiveSummarySections } from "./executive-summary-sections";
import { Card, ListItem } from "./sections";
import { SummaryIconBubble } from "./summary-icon-bubbles";

const DEFAULT_TABS: readonly ExecutiveSummaryTab[] = [
  "endpoint",
  "user",
  "email",
];

export const ExecutiveSummary = ({
  activeTabs,
}: {
  activeTabs: readonly ExecutiveSummaryTab[];
}) => (
  <div className="flex flex-col gap-8 p-10">
    <Heading />
    <Navigation activeTabs={activeTabs} />
    <ExecutiveBanner />
    <ReportColumns
      activeTabs={activeTabs.length > 0 ? activeTabs : DEFAULT_TABS}
    />
  </div>
);

const MasonryCard: ComponentType<RenderComponentProps<ReactNode>> = ({
  data,
}) => data;

const ReportColumns = ({
  activeTabs,
}: {
  activeTabs: readonly ExecutiveSummaryTab[];
}) => {
  const sections = useExecutiveSummarySections({ activeTabs });

  return (
    <Masonry
      key={activeTabs.join("-")}
      items={sections}
      render={MasonryCard}
      columnGutter={32}
      maxColumnCount={Math.min(3, sections.length)}
      className="w-full"
    />
  );
};

const summaryQuery = ({
  authConfig,
  showExcepted,
}: {
  authConfig: AuthConfig;
  showExcepted: boolean;
}) =>
  createQuery({
    queries: [
      queries.iam.getAccount(authConfig),
      enhanceQueryKey(queries.scm.summary(authConfig), showExcepted),
    ],
    queryFn: (account, summary) => ({
      company: account.company,
      endpointCount: showExcepted
        ? summary.endpoint_summary.endpoint_count
        : summary.endpoint_summary.excepted.endpoint_count,
      inboxCount: showExcepted
        ? summary.inbox_summary.inbox_count
        : summary.inbox_summary.excepted.inbox_count,
      userCount: showExcepted
        ? summary.user_summary.user_count
        : summary.user_summary.excepted.user_count,
    }),
  });

const Heading = () => {
  const authConfig = useAuthConfig();
  const { showExcepted } = useExceptedFilters();
  const {
    data: { company, endpointCount, inboxCount, userCount },
  } = summaryQuery({ authConfig, showExcepted }).useSuspenseQuery();

  const estateCount = endpointCount + inboxCount + userCount;

  return (
    <div className="grid w-full grid-cols-1 gap-10 xl:grid-cols-2">
      <div className="flex h-[240px] content-center gap-8 border-b-2 border-secondary-background">
        <Logo company={company} />
        <h1 className="flex flex-1 items-center font-serif text-[64px] leading-none font-light">
          Executive Summary
        </h1>
      </div>
      <Card className="flex justify-center p-3">
        <div className="grid w-full grid-cols-1 md:grid-cols-2">
          <div className="border-stroke-table px-6 py-3 dark:border-stroke-line md:border-r">
            <div className="mb-2 flex items-center gap-3">
              <SummaryIconBubble>
                <NetworkIcon />
              </SummaryIconBubble>
              <div className="font-serif text-[28px]">Total Estate</div>
            </div>
            <div className="text-center text-base text-secondary-foreground">
              Assets under continuous monitoring
            </div>
            <div className="text-center text-[65px] leading-none font-medium">
              <NumberFlow value={estateCount} />
            </div>
          </div>
          <div className="flex flex-col justify-center px-6 py-3">
            <HeaderSummaryCount
              label="Endpoints"
              icon={<ResourceTypeIcon resourceType="endpoint" />}
              count={endpointCount}
              overlay={["endpoints"]}
            />
            <HeaderSummaryCount
              label="Users"
              icon={<ResourceTypeIcon resourceType="user" />}
              count={userCount}
              overlay={["users"]}
            />
            <HeaderSummaryCount
              label="Inboxes"
              icon={<ResourceTypeIcon resourceType="inbox" />}
              count={inboxCount}
              overlay={["inboxes"]}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const Logo = ({ company }: { company: string }) => {
  const authConfig = useAuthConfig();
  const { data: logo } = useSuspenseQuery({
    ...queries.iam.getLogo(authConfig),
    select: (logo) =>
      logo ? `data:${logo.contentType};base64,${logo.encoded}` : undefined,
  });

  if (!logo) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 border-r border-secondary-background pr-10 text-right text-5xl">
        {company}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-1 items-center justify-center bg-white px-12 py-6">
      <img
        src={logo}
        className="h-full w-full min-w-32 object-contain"
        alt={company}
      />
    </div>
  );
};

const HeaderSummaryCount = ({
  label,
  icon,
  count,
  overlay,
}: {
  label: ReactNode;
  icon: ReactNode;
  count: number;
  overlay: NonNullable<OverlaySchema>;
}) => {
  const navigate = useNavigate();

  return (
    <ListItem className={cn("grid-cols-[2fr_1fr]", tb(label))}>
      <div className="flex items-center gap-2 font-serif text-[20px]">
        <SummaryIconBubble>{icon}</SummaryIconBubble>
        {label}
      </div>
      <div className="flex items-center justify-between text-[24px]">
        <NumberFlow value={count} />
        <IconButton
          icon={<ArrowRight className="text-secondary-foreground" />}
          onClick={() =>
            navigate({
              to: window.location.pathname,
              params: (prev) => prev,
              search: (prev) => ({ ...prev, overlay, filters: undefined }),
            })
          }
        />
      </div>
    </ListItem>
  );
};

const Navigation = ({
  activeTabs,
}: {
  activeTabs: readonly ExecutiveSummaryTab[];
}) => {
  const controls = useSortedControls();

  const isActiveControl = (control: ValidControlCode) =>
    activeTabs.length === 0 ||
    (activeTabs.includes("endpoint") &&
      (isDefensiveControl(control) ||
        isEndpointManagementControl(control) ||
        isVulnerabilityManagementControl(control))) ||
    (activeTabs.includes("user") && isIdentityProviderControl(control)) ||
    (activeTabs.includes("email") && isEmailControl(control));

  const controlNavItems = controls
    .filter((control) => !isDataControl(control.id))
    .map((control) => (
      <ControlNavItem
        key={control.instance_id}
        control={control}
        highlight={isValidControl(control.id) && isActiveControl(control.id)}
      />
    ));

  return (
    <div className="flex flex-col items-center justify-between gap-6 2xl:flex-row">
      <div className="flex items-center gap-3">
        <TabNavItem tab="endpoint" activeTabs={activeTabs} icon={ServerIcon}>
          Endpoint
        </TabNavItem>
        <TabNavItem tab="user" activeTabs={activeTabs} icon={UserIcon}>
          User
        </TabNavItem>
        <TabNavItem tab="email" activeTabs={activeTabs} icon={MailIcon}>
          Email
        </TabNavItem>
      </div>
      <div className="flex items-center gap-3">{controlNavItems}</div>
    </div>
  );
};

interface CategorizedControl extends Control {
  category: ControlCategory;
}
interface ValidCategorizedControl extends CategorizedControl {
  category: ValidControlCategory;
}

const useSortedControls = () => {
  const authConfig = useAuthConfig();
  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );

  const sortedControls = useMemo(
    () =>
      account.controls
        .map(
          (control): CategorizedControl => ({
            ...control,
            category: getControlCategory(control.id),
          }),
        )
        .filter(
          (control): control is ValidCategorizedControl =>
            control.category !== ControlCategories.INVALID,
        )
        .filter((control) => {
          const flag = flaggedControls.find(
            (f) => f.control === control.id,
          )?.flag;
          if (!flag) return true;

          return hasFeatures(account, [flag]);
        })
        .sort((a, b) => {
          const rankA = getControlCategoryRank(a.category);
          const rankB = getControlCategoryRank(b.category);
          if (rankA === rankB) {
            return getControlName(a.id).localeCompare(getControlName(b.id));
          }
          return rankA < rankB ? -1 : 1;
        }),
    [account],
  );

  return sortedControls;
};

const categories: Record<ValidControlCategory, number> = {
  [ControlCategories.XDR]: 1,
  [ControlCategories.ASSET_MANAGER]: 2,
  [ControlCategories.DISCOVERED_DEVICES]: 3,
  [ControlCategories.VULN_MANAGER]: 4,
  [ControlCategories.SIEM]: 5,
  [ControlCategories.IDENTITY]: 6,
  [ControlCategories.EMAIL]: 7,
  [ControlCategories.NETWORK]: 8,
  [ControlCategories.CLOUD]: 9,
  [ControlCategories.PRIVATE_REPO]: 10,
};

const getControlCategoryRank = (category: ValidControlCategory): number =>
  categories[category] ?? 99;

const TabNavItem = ({
  tab,
  icon: Icon,
  activeTabs,
  children,
}: {
  tab: ExecutiveSummaryTab;
  icon: FC<Omit<SVGProps<SVGSVGElement>, "ref">>;
  activeTabs: readonly ExecutiveSummaryTab[];
  children: ReactNode;
}) => {
  const isActive = activeTabs.includes(tab);

  return (
    <Link
      to="/executive-summary"
      search={(prev) => ({
        ...prev,
        tabs: isActive ? without(activeTabs, tab) : [...activeTabs, tab].sort(),
      })}
      className={cn(
        "flex items-center gap-2 rounded-full border border-stroke-table px-6 py-2 text-lg hover:bg-secondary-background dark:border-stroke-line",
        isActive &&
          "border-tertiary-background bg-tertiary-background text-white hover:bg-tertiary-background",
      )}
    >
      <Icon
        className={cn(
          "size-6 text-secondary-foreground",
          isActive && "text-white",
        )}
      />
      {children}
    </Link>
  );
};

const ControlNavItem = ({
  control,
  highlight,
}: {
  control: ValidCategorizedControl;
  highlight?: boolean;
}) => {
  const navigate = useNavigate();
  const { data: controls = [] } = useAccountControls();
  const controlCode = isValidControl(control.id) ? control.id : undefined;

  return (
    <DefaultTooltip
      asChild
      content={[getControlName(control.id), control.name?.trim()]
        .filter(Boolean)
        .join(" - ")}
    >
      <IconButton
        icon={
          <SummaryIconBubble
            className={cn(
              highlight &&
                "border-tertiary-background bg-tertiary-background text-white hover:bg-tertiary-background",
            )}
          >
            <ControlIcon
              control={control.id}
              className={cn(
                "text-secondary-foreground",
                highlight && "size-6 text-white",
              )}
            />
          </SummaryIconBubble>
        }
        onClick={() =>
          navigate({
            to: "/control-monitor",
            search: {
              active_id:
                controlCode &&
                getControlId({
                  controls,
                  category: getValidControlCategory(controlCode),
                  control: controlCode,
                  instanceId: control.instance_id,
                }),
            },
          })
        }
      />
    </DefaultTooltip>
  );
};
